import { normalizeStatus } from './workItemUtils';

export interface StatusOption {
  /** Dynamics title-case value — sent to the orchestrator on status change */
  dvValue: string;
  /** Kebab-case slug — matches what the API returns */
  value: string;
  label: string;
  color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

function opt(dvValue: string, color: StatusOption['color']): StatusOption {
  return { dvValue, value: normalizeStatus(dvValue), label: dvValue, color };
}

export const JOB_STATUSES: StatusOption[] = [
  opt('New', 'primary'),
  opt('Ready for Diagnostics', 'primary'),
  opt('In Progress', 'primary'),
  opt('Resolved', 'primary'),
  opt('Waiting for Parts', 'primary'),
  opt('Waiting on Customer', 'primary'),
  opt('Waiting on Payment', 'warning'),
  opt('With 3rd Party for Repairs', 'primary'),
  opt('Waiting on Supplier for Quote', 'primary'),
  opt('Scheduled', 'primary'),
  opt('Customer Reply', 'primary'),
  opt('Parts to be ordered', 'primary'),
  opt('Deleted', 'default'),
];

export const APPOINTMENT_STATUSES: StatusOption[] = [
  opt('Scheduled', 'primary'),
  opt('Completed', 'primary'),
  opt('No Show', 'primary'),
  opt('Cancelled', 'primary'),
  opt('Rescheduled', 'primary'),
];

// Check-In lifecycle statuses surfaced on the dashboard. Dataverse stores
// these as snake_case strings (cr001_lifecycle_status); we keep the display
// values title-case for consistency with the other status badges. Terminal
// states (cancelled, expired, checked_in) are intentionally absent — the
// backend dashboard projection filters them out so they don't clutter the
// active work-item view.
export const CHECK_IN_STATUSES: StatusOption[] = [
  { dvValue: 'draft_created', value: 'draft-created', label: 'Draft', color: 'default' },
  { dvValue: 'awaiting_customer_intake', value: 'awaiting-customer-intake', label: 'Awaiting Intake', color: 'info' },
  { dvValue: 'intake_in_progress', value: 'intake-in-progress', label: 'Intake In Progress', color: 'info' },
  { dvValue: 'terms_accepted', value: 'terms-accepted', label: 'Terms Accepted', color: 'info' },
  { dvValue: 'creating_jobs', value: 'creating-jobs', label: 'Creating Jobs', color: 'info' },
  { dvValue: 'job_creation_failed', value: 'job-creation-failed', label: 'Job Creation Failed', color: 'error' },
  { dvValue: 'awaiting_payment', value: 'awaiting-payment', label: 'Awaiting Payment', color: 'warning' },
  { dvValue: 'awaiting_bank_transfer', value: 'awaiting-bank-transfer', label: 'Awaiting Bank Transfer', color: 'warning' },
  { dvValue: 'payment_failed', value: 'payment-failed', label: 'Payment Failed', color: 'error' },
  { dvValue: 'paid', value: 'paid', label: 'Paid', color: 'success' },
  { dvValue: 'awaiting_physical_dropoff', value: 'awaiting-physical-dropoff', label: 'Awaiting Drop-off', color: 'warning' },
  { dvValue: 'inspection_in_progress', value: 'inspection-in-progress', label: 'Inspection', color: 'info' },
  { dvValue: 'checked_in', value: 'checked-in', label: 'Checked In', color: 'success' },
];

export function getStatusOptionsForType(type: string): StatusOption[] {
  if (type === 'appointment') return APPOINTMENT_STATUSES;
  if (type === 'check-in') return CHECK_IN_STATUSES;
  return JOB_STATUSES;
}

export function getStatusColor(status: string, type: string): StatusOption['color'] {
  const slug = normalizeStatus(status);
  const options = getStatusOptionsForType(type);
  return options.find(o => o.value === slug)?.color ?? 'default';
}
